// Lab 4: sharded storage, reconfiguration, and transactions.
// A ShardMaster owns a numbered history of configurations; replica groups move
// shards with an ordered handoff and run two-phase commit across groups.
//
// Deliberate abstraction, stated plainly: each replica group is one logical
// node that orders operations trivially, and the ShardMaster is one node with
// a config history. The simulator therefore does not re-simulate the
// intra-group Paxos from Lab 3. Everything it does simulate — versioned
// configs, the shard-handoff cut, prepare/commit/abort with locks, the
// reconfig/transaction barrier, and exactly-once metadata travelling with
// shards — follows the lab spec.
//
// config: { master, groups, idleGroups, shards, clients, linkDelay=1,
//           clientRetry=6, moveTimeout=5, txnTimeout=5 }
import { EventKind } from '../../core/types';
import { amoApply } from '../common/amo';
import { kvApply } from '../common/kvStore';
import { checkLab4Invariants } from './invariants';

export const MSG_ADMIN = 'admin';
export const MSG_ADMIN_REPLY = 'adminreply';
export const MSG_CONFIG_QUERY = 'configquery';
export const MSG_CONFIG = 'config';
export const MSG_ANNOUNCE = 'announce';
export const MSG_SREQUEST = 'srequest';
export const MSG_SREPLY = 'sreply';
export const MSG_REJECT = 'reject';
export const MSG_MOVE = 'move';
export const MSG_MOVE_ACK = 'moveack';
export const MSG_TXN = 'txn';
export const MSG_TXN_REPLY = 'txnreply';
export const MSG_PREPARE = 'prepare';
export const MSG_PREPARE_OK = 'prepareok';
export const MSG_PREPARE_ABORT = 'prepareabort';
export const MSG_COMMIT = 'commit';
export const MSG_COMMIT_OK = 'commitok';
export const MSG_TXN_ABORT = 'txnabort';
export const MSG_ABORT_OK = 'abortok';

export const TIMER_RETRY = 'retry';
export const TIMER_MOVE = 'move';
export const TIMER_TXN = 'txn';

export const labMeta = {
  id: 'lab4',
  title: 'Lab 4 · Sharded transactions',
  description: 'Shard movement with a clean ownership cut, plus atomic transactions across groups.',
};

export function defaultConfig() {
  return {
    master: 'M',
    groups: ['G1', 'G2'],
    idleGroups: ['G3'],
    shards: [0, 1, 2, 3],
    clients: ['C1'],
    linkDelay: 1,
    clientRetry: 6,
    moveTimeout: 5,
    txnTimeout: 5,
  };
}

export function allGroups(config) {
  return [...config.groups, ...config.idleGroups];
}

// Deterministic key placement shared by clients and groups.
export function shardOf(key, numShards) {
  return key.charCodeAt(0) % numShards;
}

export function initialize(config) {
  const shards = {};
  const perGroup = Math.ceil(config.shards.length / config.groups.length);
  config.shards.forEach((s, i) => {
    shards[s] = config.groups[Math.floor(i / perGroup)];
  });
  const first = { n: 1, shards, groups: [...config.groups] };
  const nodes = {
    [config.master]: {
      durable: { role: 'master', configs: [first] },
      volatile: {},
    },
  };
  allGroups(config).forEach((id) => {
    const member = config.groups.includes(id);
    const owned = {};
    if (member) {
      Object.keys(shards).forEach((s) => {
        if (shards[s] === id) owned[s] = { keys: {}, amo: {} };
      });
    }
    nodes[id] = {
      durable: {
        role: 'group', self: id, configN: member ? 1 : 0,
        shards: owned, appliedIds: [], served: [],
        txns: {}, locks: {},
        transfersOut: [], transfersIn: [],
        pendingReconfig: null, pendingAnnounce: null,
      },
      volatile: {},
    };
  });
  config.clients.forEach((id) => {
    nodes[id] = {
      durable: {
        role: 'sclient', config: null, nextSeq: 1, accepted: 0, sentIds: [], completed: [],
      },
      volatile: { outstanding: null, held: null },
    };
  });
  return { nodes, initialEvents: [] };
}

export function freshVolatile(durable) {
  if (durable.role === 'sclient') return { outstanding: null, held: null };
  return {};
}

function clone(part) {
  return JSON.parse(JSON.stringify(part));
}

function send(to, payload, delay, label) {
  return { to, payload, delay, label };
}

function latestConfig(node) {
  const configs = node.durable.configs;
  return configs[configs.length - 1];
}

function groupBusy(durable) {
  if (transferBusy(durable)) return true;
  return Object.values(durable.txns).some((t) =>
    ['PREPARING', 'PREPARED', 'COMMITTING', 'ABORTING', 'EARLY_COMMIT'].includes(t.state)
  );
}

// Reconfiguration and new work admission only wait on shard movement, never
// on transactions: concurrent transactions are allowed and resolve conflicts
// by aborting instead of waiting, which keeps the system deadlock-free.
function transferBusy(durable) {
  if (durable.transfersOut.length > 0 || durable.transfersIn.length > 0) return true;
  if (durable.pendingReconfig) return true;
  return false;
}

// Participants computed from a shard map (client-side or coordinator-side).
function configParticipants(cfg, keys, numShards) {
  const owners = new Set();
  keys.forEach((k) => {
    owners.add(cfg.shards[shardOf(k, numShards)]);
  });
  return [...owners].sort();
}

function coordinatorFor(participants) {
  return [...participants].sort().pop();
}

// ---------------- ShardMaster ----------------

function masterRebalance(config, latest, groups) {
  // Deterministic minimal movement: fullest group sheds its lowest shards
  // until every group holds at least floor(S/G).
  const shards = { ...latest.shards };
  const load = {};
  groups.forEach((g) => {
    load[g] = 0;
  });
  Object.keys(shards).forEach((s) => {
    if (load[shards[s]] !== undefined) load[shards[s]] += 1;
  });
  const target = Math.floor(config.shards.length / groups.length);
  groups.forEach((g) => {
    while (load[g] < target) {
      const fullest = [...groups].sort((a, b) => load[b] - load[a] || (a < b ? -1 : 1))[0];
      if (load[fullest] <= target) break;
      const shard = Object.keys(shards)
        .map(Number)
        .sort((a, b) => a - b)
        .find((s) => shards[s] === fullest);
      shards[shard] = g;
      load[fullest] -= 1;
      load[g] += 1;
    }
  });
  return shards;
}

function masterAdmin(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const latest = latestConfig(node);
  let next = null;
  if (p.adminOp === 'query') {
    const want = p.n;
    const cfg =
      want > 0 && want <= latest.n
        ? durable.configs.find((c) => c.n === want)
        : latest;
    return {
      durable,
      outbox: [
        send(event.from, { msgType: MSG_ADMIN_REPLY, config: clone(cfg) }, config.linkDelay, 'admin reply'),
      ],
      note: 'master answers query',
    };
  }
  if (p.adminOp === 'join' && !latest.groups.includes(p.group)) {
    const groups = [...latest.groups, p.group].sort();
    next = { n: latest.n + 1, shards: masterRebalance(config, latest, groups), groups };
  } else if (p.adminOp === 'leave' && latest.groups.includes(p.group) && latest.groups.length > 1) {
    const groups = latest.groups.filter((g) => g !== p.group);
    const shards = { ...latest.shards };
    const moving = Object.keys(shards).filter((s) => shards[s] === p.group);
    moving.forEach((s, i) => {
      shards[s] = groups[i % groups.length];
    });
    next = { n: latest.n + 1, shards, groups };
  } else if (
    p.adminOp === 'move' &&
    latest.groups.includes(p.group) &&
    latest.shards[p.shard] !== undefined &&
    latest.shards[p.shard] !== p.group
  ) {
    next = { n: latest.n + 1, shards: { ...latest.shards, [p.shard]: p.group }, groups: [...latest.groups] };
  }
  if (!next) {
    return {
      durable,
      outbox: [
        send(event.from, { msgType: MSG_ADMIN_REPLY, config: clone(latest), noop: true }, config.linkDelay, 'admin reply'),
      ],
      note: 'master rejects admin op',
    };
  }
  durable.configs.push(next);
  const outbox = allGroups(config)
    .filter((g) => g !== nodeId)
    .map((g) =>
      send(g, { msgType: MSG_ANNOUNCE, config: clone(next) }, config.linkDelay, 'announce config ' + next.n)
    );
  outbox.push(
    send(event.from, { msgType: MSG_ADMIN_REPLY, config: clone(next) }, config.linkDelay, 'admin reply')
  );
  return { durable, outbox, note: 'master commits config ' + next.n };
}

// ---------------- group reconfiguration ----------------

function beginReconfig(durable, next) {
  const owned = new Set(Object.keys(durable.shards).map(Number));
  const want = new Set(
    Object.keys(next.shards)
      .map(Number)
      .filter((s) => next.shards[s] === durable.self)
  );
  durable.transfersOut = [...owned]
    .filter((s) => !want.has(s))
    .map((s) => ({ shard: s, to: next.shards[s], config: next.n }));
  durable.transfersIn = [...want]
    .filter((s) => !owned.has(s))
    .map((s) => ({ shard: s, from: null, config: next.n }));
}

function tryInstall(durable) {
  if (durable.transfersOut.length > 0 || durable.transfersIn.length > 0) return null;
  const target = durable.pendingReconfig;
  if (!target) return null;
  durable.pendingReconfig = null;
  durable.configN = target.n;
  if (!target.groups.includes(durable.self)) {
    durable.shards = {};
  }
  return target;
}

function drainReconfig(durable) {
  // After transactions settle or transfers complete, apply stashed configs.
  const installed = tryInstall(durable);
  if (installed && durable.pendingAnnounce && durable.pendingAnnounce.n === durable.configN + 1) {
    const next = durable.pendingAnnounce;
    durable.pendingAnnounce = null;
    return { installed, next };
  }
  return { installed, next: null };
}

function startReconfig(config, durable, next, outbox, timerOps) {
  if (durable.configN === 0) {
    // A group joining for the first time adopts immediately; shard data
    // arrives through the normal move path.
    durable.pendingReconfig = clone(next);
    beginReconfig(durable, next);
  } else if (groupBusy(durable)) {
    durable.pendingReconfig = clone(next);
    return 'reconfig stashed behind active work';
  } else {
    durable.pendingReconfig = clone(next);
    beginReconfig(durable, next);
  }
  const { installed } = drainReconfig(durable);
  if (installed) return 'config ' + installed.n + ' installed (no movement)';
  durable.transfersOut.forEach((t) => {
    const data = durable.shards[t.shard];
    outbox.push(
      send(
        t.to,
        { msgType: MSG_MOVE, config: t.config, shard: t.shard, keys: clone(data.keys), amo: clone(data.amo) },
        config.linkDelay,
        'move shard ' + t.shard
      )
    );
    timerOps.push({
      action: 'set', timerKey: TIMER_MOVE + ':' + t.config + ':' + t.shard, timerType: TIMER_MOVE,
      payload: { config: t.config, shard: t.shard }, delay: config.moveTimeout, label: 'move timer',
    });
  });
  return 'reconfig to ' + next.n + ' starts';
}

function onAnnounce(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const next = event.payload.config;
  if (next.n <= durable.configN) return { durable, note: 'stale announce ignored' };
  if (durable.configN !== 0 && next.n > durable.configN + 1) {
    durable.pendingAnnounce = clone(next);
    return { durable, note: 'future announce stashed' };
  }
  if (durable.pendingReconfig) {
    durable.pendingAnnounce = clone(next);
    return { durable, note: 'announce queued behind reconfig' };
  }
  const outbox = [];
  const timerOps = [];
  const note = startReconfig(config, durable, next, outbox, timerOps);
  return { durable, outbox, timerOps, note };
}

function onMove(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const want = durable.transfersIn.find((t) => t.shard === p.shard && t.config === p.config);
  if (!want) {
    // Duplicate of an already-installed shard: re-ack without applying.
    const installed = durable.shards[p.shard];
    if (installed && durable.configN >= p.config - 1) {
      return {
        durable,
        outbox: [
          send(event.from, { msgType: MSG_MOVE_ACK, config: p.config, shard: p.shard }, config.linkDelay, 'move ack'),
        ],
        note: 'duplicate move re-acked',
      };
    }
    return { durable, note: 'unexpected move ignored' };
  }
  durable.shards[p.shard] = { keys: clone(p.keys), amo: clone(p.amo) };
  durable.transfersIn = durable.transfersIn.filter((t) => !(t.shard === p.shard && t.config === p.config));
  const outbox = [
    send(event.from, { msgType: MSG_MOVE_ACK, config: p.config, shard: p.shard }, config.linkDelay, 'move ack'),
  ];
  const { installed } = drainReconfig(durable);
  return { durable, outbox, note: installed ? 'shard installed; config ' + installed.n : 'shard installed' };
}

function onMoveAck(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const before = durable.transfersOut.length;
  durable.transfersOut = durable.transfersOut.filter((t) => !(t.shard === p.shard && t.config === p.config));
  if (durable.transfersOut.length === before) {
    return { durable, note: 'unexpected move ack ignored' };
  }
  delete durable.shards[p.shard];
  const timerOps = [{ action: 'cancel', timerKey: TIMER_MOVE + ':' + p.config + ':' + p.shard }];
  const { installed, next } = drainReconfig(durable);
  const outbox = [];
  const timerOps2 = [...timerOps];
  let note = 'move acked';
  if (installed) note = 'config ' + installed.n + ' installed';
  if (next) {
    const follow = startReconfig(config, durable, next, outbox, timerOps2);
    note += '; ' + follow;
  }
  return { durable, outbox, timerOps: timerOps2, note };
}

function onMoveTimer(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const t = durable.transfersOut.find((x) => x.shard === p.shard && x.config === p.config);
  if (!t) return { durable, note: 'stale move timer discarded' };
  const data = durable.shards[t.shard] || { keys: {}, amo: {} };
  return {
    durable,
    outbox: [
      send(t.to, { msgType: MSG_MOVE, config: t.config, shard: t.shard, keys: clone(data.keys), amo: clone(data.amo) }, config.linkDelay, 'move retry'),
    ],
    timerOps: [
      {
        action: 'set', timerKey: TIMER_MOVE + ':' + t.config + ':' + t.shard, timerType: TIMER_MOVE,
        payload: { config: t.config, shard: t.shard }, delay: config.moveTimeout, label: 'move timer',
      },
    ],
    note: 'move retry for shard ' + t.shard,
  };
}

// ---------------- group single-key path ----------------

function reject(to, configN, delay) {
  return send(to, { msgType: MSG_REJECT, configN }, delay, 'reject');
}

function onShardRequest(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const shard = shardOf(p.command.key, config.shards.length);
  if (p.configN !== durable.configN || transferBusy(durable) || !durable.shards[shard]) {
    return { durable, outbox: [reject(event.from, durable.configN, config.linkDelay)], note: 'request rejected' };
  }
  const store = durable.shards[shard];
  const outcome = amoApply(store.amo, p.client, p.seq, () => {
    durable.appliedIds.push(p.client + '#' + p.seq + '@' + shard);
    return kvApply(store.keys, p.command);
  });
  durable.served.push({ configN: p.configN, shard });
  return {
    durable,
    outbox: [
      send(p.client, { msgType: MSG_SREPLY, client: p.client, seq: p.seq, result: outcome.result }, config.linkDelay, 'shard reply'),
    ],
    note: outcome.status === 'executed' ? 'shard request executed' : 'duplicate served from cache',
  };
}

// ---------------- two-phase commit ----------------

function txnKeys(command) {
  const keys = [...(command.reads || [])];
  (command.writes || []).forEach((w) => keys.push(w.key));
  return [...new Set(keys)];
}

function subsetFor(command, keys) {
  const set = new Set(keys);
  return {
    reads: (command.reads || []).filter((k) => set.has(k)),
    writes: (command.writes || []).filter((w) => set.has(w.key)),
  };
}

function keysOfGroup(config, durable, keys) {
  return keys.filter((k) => durable.shards[shardOf(k, config.shards.length)]);
}

function txnRecord(id, fields) {
  return {
    id, configN: 0, client: null, seq: 0, attempt: 0, role: 'part',
    state: 'PREPARING', participants: [], keys: [], writes: [], reads: [],
    locks: [], acksPrepare: [], acksCommit: [], acksAbort: [], result: null, pendingCommit: null,
    ...fields,
  };
}

function releaseLocks(durable, txn) {
  txn.locks.forEach((k) => {
    if (durable.locks[k] === txn.id) delete durable.locks[k];
  });
  txn.locks = [];
}

function applyTxnSubset(config, durable, txn) {
  // Every owned shard touched by the txn marks execution, even read-only
  // ones; writes additionally apply through the per-shard AMO table.
  const ownedTouched = new Set();
  txn.keys.forEach((k) => {
    const shard = shardOf(k, config.shards.length);
    if (durable.shards[shard]) ownedTouched.add(shard);
  });
  const writeShards = new Set();
  txn.writes.forEach((w) => {
    const shard = shardOf(w.key, config.shards.length);
    if (durable.shards[shard]) writeShards.add(shard);
  });
  ownedTouched.forEach((shard) => {
    const mark = txn.client + '#' + txn.seq + '@' + shard;
    if (!writeShards.has(shard) && !durable.appliedIds.includes(mark)) {
      durable.appliedIds.push(mark);
    }
  });
  // Writes first, then reads (read-your-writes). AMO dedupes per shard.
  txn.writes.forEach((w) => {
    const shard = shardOf(w.key, config.shards.length);
    const store = durable.shards[shard];
    if (!store) return;
    amoApply(store.amo, txn.client, txn.seq, () => {
      durable.appliedIds.push(txn.client + '#' + txn.seq + '@' + shard);
      return kvApply(store.keys, { op: 'put', key: w.key, value: w.value });
    });
  });
  const reads = {};
  txn.reads.forEach((k) => {
    const shard = shardOf(k, config.shards.length);
    const store = durable.shards[shard];
    reads[k] = store ? store.keys[k] : undefined;
  });
  return reads;
}

function onTxnRequest(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const id = p.client + '#' + p.seq + '#' + p.attempt;
  const existing = durable.txns[id];
  if (existing) {
    if (existing.state === 'COMMITTED') {
      return {
        durable,
        outbox: [
          send(p.client, { msgType: MSG_TXN_REPLY, client: p.client, seq: p.seq, status: 'committed', result: existing.result }, config.linkDelay, 'txn reply'),
        ],
        note: 'duplicate txn answered from record',
      };
    }
    if (existing.state === 'ABORTED') {
      return {
        durable,
        outbox: [
          send(p.client, { msgType: MSG_TXN_REPLY, client: p.client, seq: p.seq, status: 'aborted' }, config.linkDelay, 'txn reply'),
        ],
        note: 'duplicate txn answered from record',
      };
    }
    return { durable, note: 'txn already running' };
  }
  const keys = txnKeys(p.command);
  if (p.configN !== durable.configN || transferBusy(durable)) {
    return { durable, outbox: [reject(event.from, durable.configN, config.linkDelay)], note: 'txn rejected' };
  }
  const participants = configParticipants({ shards: p.shards }, keys, config.shards.length);
  if (coordinatorFor(participants) !== nodeId) {
    return { durable, outbox: [reject(event.from, durable.configN, config.linkDelay)], note: 'not the coordinator' };
  }
  const mine = keysOfGroup(config, durable, keys);
  const conflict = mine.find((k) => durable.locks[k] && durable.locks[k] !== id);
  const txn = txnRecord(id, {
    configN: p.configN, client: p.client, seq: p.seq, attempt: p.attempt, role: 'coord',
    participants, keys, writes: p.command.writes || [], reads: p.command.reads || [],
    shards: { ...p.shards },
  });
  if (conflict) {
    txn.state = 'ABORTED';
    txn.result = null;
    durable.txns[id] = txn;
    return {
      durable,
      outbox: [
        send(p.client, { msgType: MSG_TXN_REPLY, client: p.client, seq: p.seq, status: 'aborted' }, config.linkDelay, 'txn reply'),
      ],
      note: 'coordinator aborts on local conflict',
    };
  }
  mine.forEach((k) => {
    durable.locks[k] = id;
  });
  txn.locks = [...mine];
  txn.state = 'PREPARING';
  txn.acksPrepare = [nodeId];
  durable.txns[id] = txn;
  const outbox = [];
  const timerOps = [];
  if (participants.length === 1) {
    commitLocal(config, durable, txn, outbox);
    durable.txns[id] = txn;
    outbox.push(
      send(p.client, { msgType: MSG_TXN_REPLY, client: p.client, seq: p.seq, status: 'committed', result: txn.result }, config.linkDelay, 'txn reply')
    );
    return { durable, outbox, note: 'single-group txn commits' };
  }
  participants
    .filter((g) => g !== nodeId)
    .forEach((g) => {
      const gkeys = keysForTxnGroup(txn, g, config);
      const subset = subsetFor(p.command, gkeys);
      outbox.push(
        send(g, {
          msgType: MSG_PREPARE, txnId: id, configN: p.configN,
          client: p.client, seq: p.seq, attempt: p.attempt,
          keys: subset.reads.concat(subset.writes.map((w) => w.key)),
          writes: subset.writes, reads: subset.reads, participants,
        }, config.linkDelay, 'prepare')
      );
    });
  timerOps.push({
    action: 'set', timerKey: TIMER_TXN + ':prepare:' + id, timerType: TIMER_TXN,
    payload: { phase: 'prepare', txnId: id }, delay: config.txnTimeout, label: 'txn timer',
  });
  return { durable, outbox, timerOps, note: 'coordinator prepares ' + id };
}

// Group membership for a key always comes from the transaction's own config,
// never the latest master config, so slices stay correct across reconfigs.
function keysForTxnGroup(txn, group, config) {
  return txn.keys.filter((k) => txn.shards[shardOf(k, config.shards.length)] === group);
}

function commitLocal(config, durable, txn, outbox) {
  const reads = applyTxnSubset(config, durable, txn);
  txn.result = { reads, writes: txn.writes.length };
  txn.state = 'COMMITTED';
  releaseLocks(durable, txn);
}

function onPrepare(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const existing = durable.txns[p.txnId];
  if (existing && existing.state === 'PREPARED') {
    return {
      durable,
      outbox: [
        send(event.from, { msgType: MSG_PREPARE_OK, txnId: p.txnId }, config.linkDelay, 'prepare ok'),
      ],
      note: 'duplicate prepare re-acked',
    };
  }
  if (existing && (existing.state === 'COMMITTED' || existing.state === 'ABORTED')) {
    return { durable, note: 'prepare for settled txn ignored' };
  }
  if (p.configN !== durable.configN || transferBusy(durable)) {
    return {
      durable,
      outbox: [
        send(event.from, { msgType: MSG_PREPARE_ABORT, txnId: p.txnId }, config.linkDelay, 'prepare abort'),
      ],
      note: 'prepare rejected',
    };
  }
  const conflict = p.keys.find((k) => durable.locks[k] && durable.locks[k] !== p.txnId);
  if (conflict) {
    return {
      durable,
      outbox: [
        send(event.from, { msgType: MSG_PREPARE_ABORT, txnId: p.txnId }, config.linkDelay, 'prepare abort'),
      ],
      note: 'prepare aborts on lock conflict',
    };
  }
  p.keys.forEach((k) => {
    durable.locks[k] = p.txnId;
  });
  const txn = existing || txnRecord(p.txnId, {
    configN: p.configN, client: p.client, seq: p.seq, attempt: p.attempt, role: 'part',
    participants: p.participants, keys: p.keys, writes: p.writes, reads: p.reads,
  });
  txn.locks = [...p.keys];
  const outbox = [
    send(event.from, { msgType: MSG_PREPARE_OK, txnId: p.txnId }, config.linkDelay, 'prepare ok'),
  ];
  if (txn.state === 'EARLY_COMMIT' && txn.pendingCommit) {
    const commit = txn.pendingCommit;
    txn.pendingCommit = null;
    txn.writes = commit.writes;
    txn.reads = commit.reads;
    const reads = applyTxnSubset(config, durable, txn);
    txn.state = 'COMMITTED';
    txn.result = { reads };
    releaseLocks(durable, txn);
    durable.txns[p.txnId] = txn;
    outbox.push(
      send(commit.from, { msgType: MSG_COMMIT_OK, txnId: p.txnId, reads }, config.linkDelay, 'commit ok')
    );
    finishTxn(config, durable, outbox, []);
    return { durable, outbox, note: 'early commit applied after prepare' };
  }
  txn.state = 'PREPARED';
  durable.txns[p.txnId] = txn;
  return { durable, outbox, note: 'participant prepared' };
}

function onPrepareOk(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const txn = durable.txns[event.payload.txnId];
  if (!txn || txn.role !== 'coord' || txn.state !== 'PREPARING') {
    return { durable, note: 'unexpected prepare ok ignored' };
  }
  if (!txn.acksPrepare.includes(event.from)) txn.acksPrepare.push(event.from);
  if (txn.acksPrepare.length < txn.participants.length) {
    return { durable, note: 'prepare ok recorded' };
  }
  txn.state = 'COMMITTING';
  const outbox = [];
  const reads = applyTxnSubset(config, durable, txn);
  txn.result = { reads, writes: txn.writes.length };
  txn.participants
    .filter((g) => g !== nodeId)
    .forEach((g) => {
      outbox.push(
        send(g, {
          msgType: MSG_COMMIT, txnId: txn.id, configN: txn.configN,
          writes: participantWrites(txn, g, config), reads: participantReads(txn, g, config),
        }, config.linkDelay, 'commit')
      );
    });
  const timerOps = [
    {
      action: 'set', timerKey: TIMER_TXN + ':commit:' + txn.id, timerType: TIMER_TXN,
      payload: { phase: 'commit', txnId: txn.id }, delay: config.txnTimeout, label: 'txn timer',
    },
    { action: 'cancel', timerKey: TIMER_TXN + ':prepare:' + txn.id },
  ];
  // Coordinator keeps its locks until every participant acks commit.
  return { durable, outbox, timerOps, note: 'coordinator commits ' + txn.id };
}

function participantWrites(txn, group, config) {
  const set = new Set(keysForTxnGroup(txn, group, config));
  return txn.writes.filter((w) => set.has(w.key));
}

function participantReads(txn, group, config) {
  const set = new Set(keysForTxnGroup(txn, group, config));
  return txn.reads.filter((k) => set.has(k));
}

function onPrepareAbort(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const txn = durable.txns[event.payload.txnId];
  if (!txn || txn.role !== 'coord' || txn.state !== 'PREPARING') {
    return { durable, note: 'unexpected prepare abort ignored' };
  }
  txn.state = 'ABORTING';
  const outbox = [];
  txn.participants
    .filter((g) => g !== nodeId)
    .forEach((g) => {
      outbox.push(send(g, { msgType: MSG_TXN_ABORT, txnId: txn.id, configN: txn.configN }, config.linkDelay, 'abort'));
    });
  const timerOps = [
    {
      action: 'set', timerKey: TIMER_TXN + ':abort:' + txn.id, timerType: TIMER_TXN,
      payload: { phase: 'abort', txnId: txn.id }, delay: config.txnTimeout, label: 'txn timer',
    },
    { action: 'cancel', timerKey: TIMER_TXN + ':prepare:' + txn.id },
  ];
  return { durable, outbox, timerOps, note: 'coordinator aborts ' + txn.id };
}

function onCommit(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  let txn = durable.txns[p.txnId];
  if (!txn) {
    txn = txnRecord(p.txnId, {
      configN: p.configN, role: 'part', state: 'EARLY_COMMIT',
      pendingCommit: { writes: p.writes, reads: p.reads, from: event.from },
    });
    durable.txns[p.txnId] = txn;
    return { durable, note: 'early commit buffered' };
  }
  if (txn.state === 'COMMITTED') {
    return {
      durable,
      outbox: [
        send(event.from, { msgType: MSG_COMMIT_OK, txnId: p.txnId, reads: (txn.result && txn.result.reads) || {} }, config.linkDelay, 'commit ok'),
      ],
      note: 'duplicate commit re-acked',
    };
  }
  if (txn.state !== 'PREPARED') {
    return { durable, note: 'commit for unprepared txn ignored' };
  }
  txn.writes = p.writes;
  txn.reads = p.reads;
  const reads = applyTxnSubset(config, durable, txn);
  txn.state = 'COMMITTED';
  txn.result = { reads };
  releaseLocks(durable, txn);
  const outbox = [
    send(event.from, { msgType: MSG_COMMIT_OK, txnId: p.txnId, reads }, config.linkDelay, 'commit ok'),
  ];
  finishTxn(config, durable, outbox, []);
  return { durable, outbox, note: 'participant commits' };
}

function onCommitOk(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const txn = durable.txns[event.payload.txnId];
  if (!txn || txn.role !== 'coord' || txn.state !== 'COMMITTING') {
    return { durable, note: 'unexpected commit ok ignored' };
  }
  if (!txn.acksCommit.includes(event.from)) txn.acksCommit.push(event.from);
  Object.assign(txn.result.reads, event.payload.reads || {});
  if (txn.acksCommit.length < txn.participants.length - 1) {
    return { durable, note: 'commit ok recorded' };
  }
  txn.state = 'COMMITTED';
  releaseLocks(durable, txn);
  const outbox = [
    send(txn.client, { msgType: MSG_TXN_REPLY, client: txn.client, seq: txn.seq, status: 'committed', result: txn.result }, config.linkDelay, 'txn reply'),
  ];
  const timerOps = [{ action: 'cancel', timerKey: TIMER_TXN + ':commit:' + txn.id }];
  finishTxn(config, durable, outbox, timerOps);
  return { durable, outbox, timerOps, note: 'txn committed' };
}

function onTxnAbort(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  let txn = durable.txns[p.txnId];
  if (!txn) {
    txn = txnRecord(p.txnId, { role: 'part', state: 'ABORTED', configN: p.configN || 0 });
    durable.txns[p.txnId] = txn;
  } else if (txn.state !== 'COMMITTED') {
    txn.state = 'ABORTED';
    releaseLocks(durable, txn);
  }
  const outbox = [
    send(event.from, { msgType: MSG_ABORT_OK, txnId: p.txnId }, config.linkDelay, 'abort ok'),
  ];
  finishTxn(config, durable, outbox, []);
  return { durable, outbox, note: 'participant aborts' };
}

function onAbortOk(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const txn = durable.txns[event.payload.txnId];
  if (!txn || txn.role !== 'coord' || txn.state !== 'ABORTING') {
    return { durable, note: 'unexpected abort ok ignored' };
  }
  if (!txn.acksAbort.includes(event.from)) txn.acksAbort.push(event.from);
  if (txn.acksAbort.length < txn.participants.length - 1) {
    return { durable, note: 'abort ok recorded' };
  }
  txn.state = 'ABORTED';
  releaseLocks(durable, txn);
  const outbox = [
    send(txn.client, { msgType: MSG_TXN_REPLY, client: txn.client, seq: txn.seq, status: 'aborted' }, config.linkDelay, 'txn reply'),
  ];
  const timerOps = [{ action: 'cancel', timerKey: TIMER_TXN + ':abort:' + txn.id }];
  finishTxn(config, durable, outbox, timerOps);
  return { durable, outbox, timerOps, note: 'txn aborted' };
}

function activeTxnStates(durable) {
  return Object.values(durable.txns).some((t) =>
    ['PREPARING', 'PREPARED', 'COMMITTING', 'ABORTING', 'EARLY_COMMIT'].includes(t.state)
  );
}

function finishTxn(config, durable, outbox, timerOps) {
  // A stashed reconfiguration may proceed now that the transaction settled.
  if (
    durable.pendingReconfig &&
    durable.transfersOut.length === 0 &&
    durable.transfersIn.length === 0 &&
    !activeTxnStates(durable)
  ) {
    const target = durable.pendingReconfig;
    durable.pendingReconfig = null;
    startReconfig(config, durable, target, outbox, timerOps);
  }
}

function onTxnTimer(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const { phase, txnId } = event.payload;
  const txn = durable.txns[txnId];
  if (!txn || txn.role !== 'coord') return { durable, note: 'stale txn timer discarded' };
  const outbox = [];
  const timerOps = [
    {
      action: 'set', timerKey: TIMER_TXN + ':' + phase + ':' + txnId, timerType: TIMER_TXN,
      payload: { phase, txnId }, delay: config.txnTimeout, label: 'txn timer',
    },
  ];
  if (phase === 'prepare' && txn.state === 'PREPARING') {
    txn.participants
      .filter((g) => g !== nodeId && !txn.acksPrepare.includes(g))
      .forEach((g) => {
        outbox.push(
          send(g, {
            msgType: MSG_PREPARE, txnId, configN: txn.configN,
            client: txn.client, seq: txn.seq, attempt: txn.attempt,
            keys: keysForTxnGroup(txn, g, config),
            writes: participantWrites(txn, g, config),
            reads: participantReads(txn, g, config),
            participants: txn.participants,
          }, config.linkDelay, 'prepare retry')
        );
      });
    return { durable, outbox, timerOps, note: 'coordinator retransmits prepare' };
  }
  if (phase === 'commit' && txn.state === 'COMMITTING') {
    txn.participants
      .filter((g) => g !== nodeId && !txn.acksCommit.includes(g))
      .forEach((g) => {
        outbox.push(
          send(g, {
            msgType: MSG_COMMIT, txnId, configN: txn.configN,
            writes: participantWrites(txn, g, config), reads: participantReads(txn, g, config),
          }, config.linkDelay, 'commit retry')
        );
      });
    return { durable, outbox, timerOps, note: 'coordinator retransmits commit' };
  }
  if (phase === 'abort' && txn.state === 'ABORTING') {
    txn.participants
      .filter((g) => g !== nodeId && !txn.acksAbort.includes(g))
      .forEach((g) => {
        outbox.push(send(g, { msgType: MSG_TXN_ABORT, txnId, configN: txn.configN }, config.linkDelay, 'abort retry'));
      });
    return { durable, outbox, timerOps, note: 'coordinator retransmits abort' };
  }
  return { durable, note: 'stale txn timer discarded' };
}

// ---------------- client ----------------

function clientRoute(durable, volatile, nodeId, config) {
  const cmd = volatile.held.command;
  if (cmd.op === 'multi') {
    const keys = txnKeys(cmd);
    const participants = configParticipants(durable.config, keys, config.shards.length);
    const coord = coordinatorFor(participants);
    const seq = durable.nextSeq;
    durable.nextSeq += 1;
    durable.accepted += 1;
    durable.sentIds.push(nodeId + '#' + seq);
    volatile.outstanding = { seq, command: cmd, attempt: 1, kind: 'txn' };
    volatile.held = null;
    return {
      durable,
      volatile,
      outbox: [
        send(coord, { msgType: MSG_TXN, configN: durable.config.n, shards: { ...durable.config.shards }, client: nodeId, seq, attempt: 1, command: cmd }, config.linkDelay, 'txn request'),
      ],
      timerOps: [
        {
          action: 'set', timerKey: TIMER_RETRY + ':' + seq, timerType: TIMER_RETRY,
          payload: { seq }, delay: config.clientRetry, label: 'client retry',
        },
      ],
      note: 'client starts txn seq ' + seq,
    };
  }
  const shard = shardOf(cmd.key, config.shards.length);
  const owner = durable.config.shards[shard];
  const seq = durable.nextSeq;
  durable.nextSeq += 1;
  durable.accepted += 1;
  durable.sentIds.push(nodeId + '#' + seq);
  volatile.outstanding = { seq, command: cmd, attempt: 1, kind: 'single' };
  volatile.held = null;
  return {
    durable,
    volatile,
    outbox: [
      send(owner, { msgType: MSG_SREQUEST, configN: durable.config.n, client: nodeId, seq, command: cmd }, config.linkDelay, 'shard request'),
    ],
    timerOps: [
      {
        action: 'set', timerKey: TIMER_RETRY + ':' + seq, timerType: TIMER_RETRY,
        payload: { seq }, delay: config.clientRetry, label: 'client retry',
      },
    ],
    note: 'client sends seq ' + seq,
  };
}

function clientResend(durable, volatile, nodeId, config) {
  const out = volatile.outstanding;
  if (out.kind === 'txn') {
    const keys = txnKeys(out.command);
    const participants = configParticipants(durable.config, keys, config.shards.length);
    const coord = coordinatorFor(participants);
    return send(coord, { msgType: MSG_TXN, configN: durable.config.n, shards: { ...durable.config.shards }, client: nodeId, seq: out.seq, attempt: out.attempt, command: out.command }, config.linkDelay, 'txn request');
  }
  const shard = shardOf(out.command.key, config.shards.length);
  const owner = durable.config.shards[shard];
  return send(owner, { msgType: MSG_SREQUEST, configN: durable.config.n, client: nodeId, seq: out.seq, command: out.command }, config.linkDelay, 'shard request');
}

export function handleClientCommand(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node || node.durable.role !== 'sclient') {
    return { note: 'command sent to non-client; ignored' };
  }
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  if (volatile.outstanding || volatile.held) {
    return { durable, volatile, note: 'client busy; command ignored' };
  }
  const command = event.payload.command;
  if (command.op === 'admin') {
    volatile.held = null;
    const seq = durable.nextSeq;
    durable.nextSeq += 1;
    durable.accepted += 1;
    durable.sentIds.push(nodeId + '#' + seq);
    volatile.outstanding = { seq, command, attempt: 1, kind: 'admin' };
    return {
      durable,
      volatile,
      outbox: [
        send(config.master, { msgType: MSG_ADMIN, adminOp: command.adminOp, group: command.group, shard: command.shard, n: command.n, client: nodeId, seq }, config.linkDelay, 'admin request'),
      ],
      timerOps: [
        {
          action: 'set', timerKey: TIMER_RETRY + ':' + seq, timerType: TIMER_RETRY,
          payload: { seq }, delay: config.clientRetry, label: 'client retry',
        },
      ],
      note: 'client sends admin ' + command.adminOp,
    };
  }
  volatile.held = { command };
  if (durable.config) {
    return clientRoute(durable, volatile, nodeId, config);
  }
  return {
    durable,
    volatile,
    outbox: [
      send(config.master, { msgType: MSG_CONFIG_QUERY }, config.linkDelay, 'config query'),
    ],
    note: 'client queries config',
  };
}

function onClientMessage(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  if (p.msgType === MSG_CONFIG || (p.msgType === MSG_ADMIN_REPLY && p.config)) {
    if (p.config) durable.config = clone(p.config);
    if (volatile.held) {
      return clientRoute(durable, volatile, nodeId, config);
    }
    if (volatile.outstanding && volatile.outstanding.kind !== 'admin') {
      return {
        durable,
        volatile,
        outbox: [clientResend(durable, volatile, nodeId, config)],
        note: 'client resends with config ' + durable.config.n,
      };
    }
    if (volatile.outstanding && volatile.outstanding.kind === 'admin') {
      durable.completed.push({ seq: volatile.outstanding.seq, result: { config: durable.config.n }, kind: 'admin' });
      volatile.outstanding = null;
      return {
        durable,
        volatile,
        timerOps: [{ action: 'cancel', timerKey: TIMER_RETRY + ':' + durable.completed[durable.completed.length - 1].seq }],
        note: 'admin completes',
      };
    }
    return { durable, volatile, note: 'config cached' };
  }
  if (p.msgType === MSG_SREPLY) {
    if (volatile.outstanding && volatile.outstanding.seq === p.seq) {
      durable.completed.push({ seq: p.seq, result: p.result, kind: 'data' });
      volatile.outstanding = null;
      return {
        durable,
        volatile,
        timerOps: [{ action: 'cancel', timerKey: TIMER_RETRY + ':' + p.seq }],
        note: 'client completes seq ' + p.seq,
      };
    }
    return { durable, volatile, note: 'stale shard reply ignored' };
  }
  if (p.msgType === MSG_TXN_REPLY) {
    if (!volatile.outstanding || volatile.outstanding.seq !== p.seq) {
      return { durable, volatile, note: 'stale txn reply ignored' };
    }
    if (p.status === 'committed') {
      durable.completed.push({ seq: p.seq, result: p.result, kind: 'data' });
      volatile.outstanding = null;
      return {
        durable,
        volatile,
        timerOps: [{ action: 'cancel', timerKey: TIMER_RETRY + ':' + p.seq }],
        note: 'client commits seq ' + p.seq,
      };
    }
    volatile.outstanding.attempt += 1;
    return {
      durable,
      volatile,
      outbox: [clientResend(durable, volatile, nodeId, config)],
      timerOps: [
        {
          action: 'set', timerKey: TIMER_RETRY + ':' + p.seq, timerType: TIMER_RETRY,
          payload: { seq: p.seq }, delay: config.clientRetry, label: 'client retry',
        },
      ],
      note: 'txn aborted; client retries attempt ' + volatile.outstanding.attempt,
    };
  }
  if (p.msgType === MSG_REJECT) {
    if (!volatile.outstanding) return { durable, volatile, note: 'stale reject ignored' };
    return {
      durable,
      volatile,
      outbox: [
        send(config.master, { msgType: MSG_CONFIG_QUERY }, config.linkDelay, 'config query'),
      ],
      note: 'client refreshes config after reject',
    };
  }
  return { durable, volatile, note: 'unexpected client message ignored' };
}

function onClientRetry(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const { seq } = event.payload;
  if (!volatile.outstanding || volatile.outstanding.seq !== seq) {
    return { durable, volatile, note: 'stale client timer discarded' };
  }
  const out = volatile.outstanding;
  const rearm = {
    action: 'set', timerKey: TIMER_RETRY + ':' + seq, timerType: TIMER_RETRY,
    payload: { seq }, delay: config.clientRetry, label: 'client retry',
  };
  if (out.kind === 'admin') {
    return {
      durable,
      volatile,
      outbox: [
        send(config.master, { msgType: MSG_ADMIN, adminOp: out.command.adminOp, group: out.command.group, shard: out.command.shard, n: out.command.n, client: nodeId, seq }, config.linkDelay, 'admin retry'),
      ],
      timerOps: [rearm],
      note: 'client retries admin',
    };
  }
  if (!durable.config) {
    return {
      durable,
      volatile,
      outbox: [
        send(config.master, { msgType: MSG_CONFIG_QUERY }, config.linkDelay, 'config query'),
      ],
      timerOps: [rearm],
      note: 'client re-queries config',
    };
  }
  return {
    durable,
    volatile,
    outbox: [clientResend(durable, volatile, nodeId, config)],
    timerOps: [rearm],
    note: 'client retries seq ' + seq,
  };
}

// ---------------- adapter ----------------

export function handleMessage(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node) return { note: 'message to unknown node; ignored' };
  const role = node.durable.role;
  const t = event.payload.msgType;
  if (role === 'master') {
    if (t === MSG_ADMIN) return masterAdmin(state, config, nodeId, event);
    if (t === MSG_CONFIG_QUERY) {
      return {
        outbox: [
          send(event.from, { msgType: MSG_CONFIG, config: clone(latestConfig(node)) }, config.linkDelay, 'config reply'),
        ],
        note: 'master answers config query',
      };
    }
    return {};
  }
  if (role === 'group') {
    switch (t) {
      case MSG_ANNOUNCE:
        return onAnnounce(state, config, nodeId, event);
      case MSG_MOVE:
        return onMove(state, config, nodeId, event);
      case MSG_MOVE_ACK:
        return onMoveAck(state, config, nodeId, event);
      case MSG_SREQUEST:
        return onShardRequest(state, config, nodeId, event);
      case MSG_TXN:
        return onTxnRequest(state, config, nodeId, event);
      case MSG_PREPARE:
        return onPrepare(state, config, nodeId, event);
      case MSG_PREPARE_OK:
        return onPrepareOk(state, config, nodeId, event);
      case MSG_PREPARE_ABORT:
        return onPrepareAbort(state, config, nodeId, event);
      case MSG_COMMIT:
        return onCommit(state, config, nodeId, event);
      case MSG_COMMIT_OK:
        return onCommitOk(state, config, nodeId, event);
      case MSG_TXN_ABORT:
        return onTxnAbort(state, config, nodeId, event);
      case MSG_ABORT_OK:
        return onAbortOk(state, config, nodeId, event);
      default:
        return { note: 'unexpected group message ignored' };
    }
  }
  if (role === 'sclient') {
    return onClientMessage(state, config, nodeId, event);
  }
  return {};
}

export function handleTimer(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node) return { note: 'timer for unknown node; ignored' };
  if (node.durable.role === 'sclient') {
    if (event.payload.timerType === TIMER_RETRY) return onClientRetry(state, config, nodeId, event);
    return { note: 'unknown timer; ignored' };
  }
  if (node.durable.role === 'group') {
    if (event.payload.timerType === TIMER_MOVE) return onMoveTimer(state, config, nodeId, event);
    if (event.payload.timerType === TIMER_TXN) return onTxnTimer(state, config, nodeId, event);
  }
  return { note: 'unknown timer; ignored' };
}

export function checkInvariants(state) {
  return checkLab4Invariants(state);
}

export function describeEvent(event) {
  const p = event.payload || {};
  if (event.kind === EventKind.MESSAGE) {
    switch (p.msgType) {
      case MSG_ADMIN:
        return 'deliver admin ' + p.adminOp;
      case MSG_ADMIN_REPLY:
        return 'deliver admin reply';
      case MSG_CONFIG_QUERY:
        return 'deliver config query';
      case MSG_CONFIG:
        return 'deliver config';
      case MSG_ANNOUNCE:
        return 'deliver announce config ' + (p.config && p.config.n);
      case MSG_SREQUEST:
        return 'deliver shard request ' + p.client + '#' + p.seq;
      case MSG_SREPLY:
        return 'deliver shard reply ' + p.client + '#' + p.seq;
      case MSG_REJECT:
        return 'deliver reject';
      case MSG_MOVE:
        return 'deliver move shard ' + p.shard;
      case MSG_MOVE_ACK:
        return 'deliver move ack shard ' + p.shard;
      case MSG_TXN:
        return 'deliver txn request ' + p.client + '#' + p.seq;
      case MSG_TXN_REPLY:
        return 'deliver txn reply (' + p.status + ')';
      case MSG_PREPARE:
        return 'deliver prepare ' + p.txnId;
      case MSG_PREPARE_OK:
        return 'deliver prepare ok ' + p.txnId;
      case MSG_PREPARE_ABORT:
        return 'deliver prepare abort ' + p.txnId;
      case MSG_COMMIT:
        return 'deliver commit ' + p.txnId;
      case MSG_COMMIT_OK:
        return 'deliver commit ok ' + p.txnId;
      case MSG_TXN_ABORT:
        return 'deliver abort ' + p.txnId;
      case MSG_ABORT_OK:
        return 'deliver abort ok ' + p.txnId;
      default:
        return 'deliver message';
    }
  }
  if (event.kind === EventKind.TIMER) return 'fire timer ' + (p.timerKey || '');
  if (event.kind === EventKind.CLIENT_COMMAND) return 'submit command';
  return event.label || event.kind;
}

export function summarizeNode(nodeId, node) {
  const { durable } = node;
  if (durable.role === 'master') {
    const latest = durable.configs[durable.configs.length - 1];
    return [
      ['role', 'shard master'],
      ['configs', durable.configs.map((c) => c.n).join(', ')],
      ['latest', 'v' + latest.n + ' ' + JSON.stringify(latest.shards)],
    ];
  }
  if (durable.role === 'group') {
    const txns = Object.values(durable.txns).filter((t) =>
      ['PREPARING', 'PREPARED', 'COMMITTING', 'ABORTING', 'EARLY_COMMIT'].includes(t.state)
    );
    return [
      ['role', 'replica group'],
      ['config', 'v' + durable.configN],
      ['shards', Object.keys(durable.shards).join(', ') || '(none)'],
      ['keys', Object.keys(durable.shards).map((s) => s + ':' + Object.keys(durable.shards[s].keys).join(',')).join(' ') || '(empty)'],
      ['active txns', txns.map((t) => t.id + ':' + t.state).join(', ') || 'none'],
      ['locks', Object.keys(durable.locks).join(', ') || 'none'],
      ['transfers', 'out:' + durable.transfersOut.length + ' in:' + durable.transfersIn.length],
      ['applied', String(durable.appliedIds.length)],
    ];
  }
  return [
    ['role', 'client'],
    ['config', durable.config ? 'v' + durable.config.n : '(none)'],
    ['completed', String(durable.completed.length)],
    ['outstanding', node.volatile.outstanding ? '#' + node.volatile.outstanding.seq : 'none'],
  ];
}

export function sampleCommands(config) {
  const client = config.clients[0];
  return [
    { label: 'PUT x = 1', to: client, command: { op: 'put', key: 'x', value: 1 } },
    { label: 'GET x', to: client, command: { op: 'get', key: 'x' } },
    {
      label: 'MultiPut a, b',
      to: client,
      command: { op: 'multi', writes: [{ key: 'a', value: 10 }, { key: 'b', value: 20 }] },
    },
    {
      label: 'MultiGet a, b',
      to: client,
      command: { op: 'multi', reads: ['a', 'b'] },
    },
    { label: 'Join G3', to: client, command: { op: 'admin', adminOp: 'join', group: 'G3' } },
    { label: 'Move shard 1 to G2', to: client, command: { op: 'admin', adminOp: 'move', shard: 1, group: 'G2' } },
    { label: 'Query latest config', to: client, command: { op: 'admin', adminOp: 'query', n: -1 } },
  ];
}

const lab4Adapter = {
  initialize,
  handleMessage,
  handleClientCommand,
  handleTimer,
  freshVolatile,
  checkInvariants,
  describeEvent,
  summarizeNode,
};

export default lab4Adapter;
